import React, { useEffect, useMemo, useState } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Input,
  MenuItem,
  Select,
  Tooltip,
} from "@material-ui/core";
import { Visibility } from "@material-ui/icons";

import { getContent, getContents } from "../../crud/api/content.crud";
import * as contentsDUCK from "../../../redux/ducks/contents.duck";
import { setLoader } from "../../../redux/ducks/load.duck";
import FilterDataTable from "../../utils/FilterDataTable";
import { setAlert } from "../../../redux/ducks/alert.duck";
import CustomTable from "../../utils/CustomTable";
import { columns } from "./utils";

import CardViewContentStaff from "../../Components/Announcements/CardViewContentStaff";
import CardPaginator from "../../Components/CardPaginator";
import { getFocusAreas } from "../../crud/api/focus_areas.crud";
import NotifyModal from "../Contens/NotifyModal";
import { isEmpty } from "lodash";
import notice from "../../utils/notice";

const ContentsStaff = ({
  isGrid,
  contents,
  contentActions,
  settingLoader,
  school,
}) => {
  const [modalContent, setModalContent] = useState({
    openModal: false,
    content: "",
    type: "",
  });
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  // TZ-798: page size for both the card grid and the table. Previously the
  // endpoint was called with per_page=500, so the card view rendered up to 500
  // cards at once with no pager → blank/laggy. Keep it small and paginate.
  const PER_PAGE = 12;

  const isProAthleteContent = Boolean(school?.is_pro_athlete);

  const [focusAreas, setFocusAreas] = useState([]);
  const [filteredFocusAreas, setFilteredFocusAreas] = useState([]);

  const [selectedAnnc, setSelectedAnnc] = useState(null);

  const schoolSlug = school?.slug;
  const schoolIsProAthlete = school?.is_pro_athlete;

  useEffect(() => {
    if (!schoolSlug) return;
    getFocusAreas(setFocusAreas, schoolSlug);
  }, [schoolSlug]);

  useEffect(() => {
    if (schoolIsProAthlete === undefined) return;
    const filtered = focusAreas.filter(
      (fa) => fa.is_pro_athlete === schoolIsProAthlete
    );
    setFilteredFocusAreas(filtered);
  }, [focusAreas, schoolIsProAthlete]);

  useEffect(() => {
    settingLoader(true);
    getContents(
      contentActions.setContents,
      page,
      1,
      query,
      isProAthleteContent,
      tabValue,
      PER_PAGE
    ).catch((err) => {
      console.error("Error loading contents:", err);
    }).finally(() => {
      settingLoader(false);
    });
  }, [contentActions.setContents, page, tabValue, query, settingLoader, isProAthleteContent]);

  const finalColumns = useMemo(() => {
    const cols = [...columns];
    if (tabValue !== 4) {
      cols.splice(
        3,
        0,
        {
          name: "duration",
          label: "Duration",
          options: {
            filter: false,
            sort: false,
          },
        },
        {
          name: "author",
          label: "Author",
          options: {
            filter: false,
            sort: false,
            customBodyRender: (value) => value && <>{value.author}</>,
          },
        }
      );
    }
    return cols;
  }, [tabValue]);

  const actionColumns = [
    {
      name: "resource_url",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, meta, updater) => {
          return (
            <div className="d-flex">
              <Tooltip title="View Content" placement="top">
                {meta.rowData[2] ? (
                  meta.rowData[2] === "html" ? (
                    <IconButton
                      id={`view_content_btn_${meta.rowData[0]}`}
                      name="content"
                      size="small"
                      aria-label="View Content"
                      onClick={() =>
                        setModalContent({
                          openModal: true,
                          content: value,
                          type: "html",
                        })
                      }
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  ) : (
                    <a
                      id={`view_content_btn_${meta.rowData[0]}`}
                      href={value}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-light btn-hover-success text-success btn-sm mx-3"
                      name="content"
                      aria-label="View content"
                    >
                      <Visibility fontSize="small" />
                    </a>
                  )
                ) : (
                  <button
                    id={`view_content_btn_${meta.rowData[0]}`}
                    className="btn btn-light btn-hover-success text-success btn-sm mx-3"
                    disabled
                    aria-label="View content"
                    aria-disabled="true"
                  >
                    <Visibility fontSize="small" />
                  </button>
                )}
              </Tooltip>
            </div>
          );
        },
      },
    },
  ];
  const onSearch = () => {
    // The data effect already refetches on query/page change — just reset to
    // page 1 so the new search starts at the first page.
    setPage(1);
  };

  // TZ-798: card body click opens the resource. Mirrors the table's view action:
  // html opens in the modal, other file types open the URL in a new tab.
  const view = (id) => {
    const item = contents.data.find((c) => c.id === id);
    if (!item || !item.resource_url) return;
    if (item.file_type === "html") {
      setModalContent({
        openModal: true,
        content: item.resource_url,
        type: "html",
      });
    } else {
      window.open(item.resource_url, "_blank", "noopener,noreferrer");
    }
  };

  const FilePreview = ({ type, url }) => {
    switch (type) {
      case "document": // Word, Excel, etc.
      case "pdf":
        return (
          <iframe
            title="Document viewer"
            src={`https://docs.google.com/viewer?url=${encodeURIComponent(
              url
            )}&embedded=true`}
            style={{ width: "100%", height: "500px", border: "none" }}
          />
        );

      case "image":
        if (isEmpty(url)) {
          return <span>No content available</span>;
        }
        return (
          <img
            src={url}
            alt="preview"
            style={{ maxHeight: "400px", width: "auto", display: "block" }}
          />
        );

      case "text":
        return (
          <iframe
            src={url}
            title="preview"
            style={{ width: "100%", height: "400px", border: "none" }}
          />
        );

      case "video":
        return (
          <video
            src={url}
            controls
            style={{ width: "100%", maxHeight: "400px", display: "block" }}
          />
        );

      case "audio":
        return (
          <audio
            src={url}
            controls
            style={{ width: "100%", display: "block" }}
          />
        );

      case "link":
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "blue", textDecoration: "underline" }}
          >
            {url}
          </a>
        );

      default:
        return <span dangerouslySetInnerHTML={{ __html: url }} />;
    }
  };

  const HtmlDialogContent = () => (
    <Dialog
      id="view_content_dialog"
      open={modalContent.openModal}
      onClose={() => setModalContent({ content: "", openModal: false })}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>Content</DialogTitle>
      <DialogContent
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <FilePreview type={modalContent.type} url={modalContent.content} />
      </DialogContent>
    </Dialog>
  );

  const handleOpenNotify = async (id) => {
    try {
      const annc = await getContent(contentActions.setContent, id);
      const data = annc?.payload ?? annc;
      if (!data?.id) {
        notice("Error loading content");
        return;
      }
      setSelectedAnnc({
        contentId: data.id,
        contentTitle: data.title,
      });
      setShowNotificationModal(true);
    } catch (err) {
      console.error(err);
      notice("Error loading content");
    }
  };

  return (
    <>
      <Grid container style={{ padding: "20px 50px 0 50px" }}>
        <Grid item sx={12} md={6}>
          <FilterDataTable
            query={query}
            setQuery={setQuery}
            search={onSearch}
          />
        </Grid>
        <Grid item sx={12} md={6}>
          <Select
            id="focus_area_select"
            label="Focus area"
            name="focus_area_select"
            value={tabValue}
            input={<Input variant="outlined" />}
            fullWidth
            onChange={(e) => {
              setPage(1);
              setTabValue(e.target.value);
            }}
          >
            <MenuItem value={0}>All</MenuItem>
            {filteredFocusAreas.map((fa) => (
              <MenuItem key={fa.id} value={fa.id}>
                {fa.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>

      {isGrid ? (
        <CustomTable
          title="Polls"
          columns={[...finalColumns, ...actionColumns]}
          data={contents.data}
          page={contents.page}
          count={contents.count}
          rowsPerPage={PER_PAGE}
          setPage={setPage}
        />
      ) : (
        <>
          <CardViewContentStaff
            data={contents.data}
            view={view}
            setModalContent={setModalContent}
            handleOpenNotificationModal={handleOpenNotify}
          />
          {/* TZ-798: pager for the card grid (the table view has its own). */}
          <CardPaginator
            page={contents.page}
            count={contents.count}
            rowsPerPage={PER_PAGE}
            onChangePage={setPage}
          />
        </>
      )}
      <HtmlDialogContent />
      <NotifyModal
        open={showNotificationModal}
        setOpen={setShowNotificationModal}
        isContentProAthlete={isProAthleteContent}
        customContent={selectedAnnc}
      />
    </>
  );
};

const mapStateToProps = (store) => {
  return {
    contents: store.contents_.contents,
    school: store.school.selected_school,
  };
};

const mapDispatchToProps = (dispatch) => ({
  settingLoader: bindActionCreators(setLoader, dispatch),
  contentActions: bindActionCreators(contentsDUCK.actions, dispatch),
  setAlert: bindActionCreators(setAlert, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ContentsStaff);
